import { useState, type FormEvent } from 'react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';

type Mode = 'signin' | 'forgot';

export default function Login() {
  const { signIn } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    const { error: msg } = await signIn(email, password);
    if (msg) setError(msg);
    setSubmitting(false);
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setNotice(null);
    // Supabase emails a reset link that lands back here with a recovery token
    // in the URL; supabase-js's detectSessionInUrl picks it up and emits
    // PASSWORD_RECOVERY via onAuthStateChange. The AuthContext handles it.
    const { error: msg } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (msg) {
      setError(msg.message);
    } else {
      setNotice('Check your email for the reset link.');
    }
    setSubmitting(false);
  };

  if (mode === 'forgot') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <form onSubmit={handleForgot} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">Reset password</h1>
            <p className="text-sm text-slate-400">We'll email you a link.</p>
          </div>
          <label className="block">
            <span className="text-xs text-slate-400">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              autoFocus
              className="input mt-1"
            />
          </label>
          {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</div>}
          {notice && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">{notice}</div>}
          <button
            type="submit"
            disabled={submitting || !email}
            className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-800 disabled:text-slate-500"
          >
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>
          <button
            type="button"
            onClick={() => { setMode('signin'); setError(null); setNotice(null); }}
            className="w-full text-xs text-slate-400 hover:text-slate-200"
          >
            Back to sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form onSubmit={handleSignIn} className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">Floosi</h1>
          <p className="text-sm text-slate-400">Sign in to continue.</p>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="text-xs text-slate-400">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required autoFocus className="input mt-1" />
          </label>
          <label className="block">
            <span className="text-xs text-slate-400">Password</span>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required className="input mt-1" />
          </label>
        </div>

        {error && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</div>}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="w-full py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-sm font-medium disabled:bg-slate-800 disabled:text-slate-500"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <button
          type="button"
          onClick={() => { setMode('forgot'); setError(null); setNotice(null); }}
          className="w-full text-xs text-slate-400 hover:text-slate-200"
        >
          Forgot password?
        </button>

        <p className="text-xs text-slate-500 text-center">
          Single-user app. Public signups are disabled.
        </p>
      </form>
    </div>
  );
}
